"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { Calendar, Briefcase, Building2, MessageSquare, Loader as Loader2, CircleCheck as CheckCircle2, CircleAlert as AlertCircle } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
function GeneralContactForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-4">
        <CheckCircle2 className="size-12 text-primary" />
        <h3 className="text-xl font-default font-semibold text-foreground">Message Sent</h3>
        <p className="text-muted-foreground">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-4">
          Send Another Message
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <FormField control={control} name="firstName" rules={{
        required: "First name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>First Name</FormLabel>
              <FormControl>
                <Input placeholder="John" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
        <FormField control={control} name="lastName" rules={{
        required: "Last name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Last Name</FormLabel>
              <FormControl>
                <Input placeholder="Doe" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="email" rules={{
      required: "Email is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Please enter a valid email address"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel>Email Address</FormLabel>
            <FormControl>
              <Input type="email" placeholder="john@example.com" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "Message is required",
      minLength: {
        value: 10,
        message: "Message must be at least 10 characters"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel>How can we help?</FormLabel>
            <FormControl>
              <Textarea placeholder="Tell us about your inquiry..." className="min-h-[120px]" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-3 rounded-md bg-destructive/10 text-destructive flex items-center gap-2 text-sm">
          <AlertCircle className="size-4" />
          <p>{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full">
        {isSubmitting ? <>
            <Loader2 className="mr-2 size-4 animate-spin" />
            Sending...
          </> : "Send Message"}
      </Button>
    </Form>;
}
function BusinessInquiryForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-4">
        <CheckCircle2 className="size-12 text-primary" />
        <h3 className="text-xl font-default font-semibold text-foreground">Inquiry Received</h3>
        <p className="text-muted-foreground">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-4">
          Submit Another Inquiry
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-4">
      <FormField control={control} name="companyName" rules={{
      required: "Company name is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel>Company Name</FormLabel>
            <FormControl>
              <Input placeholder="Acme Corp" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <FormField control={control} name="contactName" rules={{
        required: "Contact name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Contact Name</FormLabel>
              <FormControl>
                <Input placeholder="Jane Smith" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
        <FormField control={control} name="workEmail" rules={{
        required: "Work email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Work Email</FormLabel>
              <FormControl>
                <Input type="email" placeholder="jane@acmecorp.com" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="partnershipDetails" rules={{
      required: "Partnership details are required",
      minLength: {
        value: 20,
        message: "Please provide more details (min 20 characters)"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel>Partnership / Sales Details</FormLabel>
            <FormControl>
              <Textarea placeholder="Describe the nature of your business inquiry..." className="min-h-[120px]" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-3 rounded-md bg-destructive/10 text-destructive flex items-center gap-2 text-sm">
          <AlertCircle className="size-4" />
          <p>{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full">
        {isSubmitting ? <>
            <Loader2 className="mr-2 size-4 animate-spin" />
            Submitting...
          </> : "Submit Business Inquiry"}
      </Button>
    </Form>;
}
function RecruitingForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-4">
        <CheckCircle2 className="size-12 text-primary" />
        <h3 className="text-xl font-default font-semibold text-foreground">Application Submitted</h3>
        <p className="text-muted-foreground">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-4">
          Submit Another Application
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <FormField control={control} name="fullName" rules={{
        required: "Full name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Full Name</FormLabel>
              <FormControl>
                <Input placeholder="Alex Johnson" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="alex@example.com" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="role" rules={{
      required: "Role is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel>Role Interested In</FormLabel>
            <FormControl>
              <Input placeholder="e.g. Senior Developer, Marketing Manager" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="linkedin" rules={{
      pattern: {
        value: /^https?:\/\/(www\.)?linkedin\.com\/.*$/,
        message: "Please enter a valid LinkedIn URL"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel>LinkedIn Profile (Optional)</FormLabel>
            <FormControl>
              <Input type="url" placeholder="https://linkedin.com/in/alexjohnson" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="coverLetter" rules={{
      required: "A brief message or cover letter is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel>Cover Letter / Message</FormLabel>
            <FormControl>
              <Textarea placeholder="Tell us why you'd be a great fit..." className="min-h-[120px]" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-3 rounded-md bg-destructive/10 text-destructive flex items-center gap-2 text-sm">
          <AlertCircle className="size-4" />
          <p>{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full">
        {isSubmitting ? <>
            <Loader2 className="mr-2 size-4 animate-spin" />
            Submitting...
          </> : "Submit Application"}
      </Button>
    </Form>;
}
export default function MultipleRequestForms() {
  return <section id="multiple-request-forms" className="py-16 md:py-24 bg-background">
      <div className="container mx-auto px-4 max-w-4xl">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-default font-bold text-foreground mb-4">
            How Can We Help You?
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Select the option that best describes your inquiry below, or schedule a direct consultation with our team.
          </p>
        </div>

        <Tabs defaultValue="general" className="w-full">
          <div className="flex justify-center mb-8">
            <TabsList className="flex flex-col sm:flex-row h-auto w-full sm:w-auto gap-2 sm:gap-0 bg-transparent sm:bg-muted p-0 sm:p-[3px]">
              <TabsTrigger value="general" className="w-full sm:w-auto py-3 sm:py-1.5 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground sm:data-[state=active]:bg-background sm:data-[state=active]:text-foreground border sm:border-transparent border-border sm:border-none rounded-md sm:rounded-sm">
                <MessageSquare className="size-4 mr-2" />
                General Inquiry
              </TabsTrigger>
              <TabsTrigger value="business" className="w-full sm:w-auto py-3 sm:py-1.5 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground sm:data-[state=active]:bg-background sm:data-[state=active]:text-foreground border sm:border-transparent border-border sm:border-none rounded-md sm:rounded-sm">
                <Building2 className="size-4 mr-2" />
                Partnership / Sales
              </TabsTrigger>
              <TabsTrigger value="recruiting" className="w-full sm:w-auto py-3 sm:py-1.5 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground sm:data-[state=active]:bg-background sm:data-[state=active]:text-foreground border sm:border-transparent border-border sm:border-none rounded-md sm:rounded-sm">
                <Briefcase className="size-4 mr-2" />
                Hiring Request
              </TabsTrigger>
            </TabsList>
          </div>

          <Card className="border-border shadow-md">
            <TabsContent value="general" className="m-0">
              <CardHeader>
                <CardTitle className="font-default text-2xl">Contact Us</CardTitle>
                <CardDescription>
                  Have a general question or feedback? Fill out the form below and our team will get back to you shortly.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <FormProvider formId="inquiry-form-general" sectionName="Multiple Request Forms" defaultValues={{
                firstName: "",
                lastName: "",
                email: "",
                message: ""
              }} submitText="Send Message" successMessage="Thank you for reaching out! We will get back to you within 24 hours.">
                  <GeneralContactForm />
                </FormProvider>
              </CardContent>
            </TabsContent>

            <TabsContent value="business" className="m-0">
              <CardHeader>
                <CardTitle className="font-default text-2xl">Business Inquiry</CardTitle>
                <CardDescription>
                  Interested in partnering with us or exploring our enterprise solutions? Let's discuss how we can work together.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <FormProvider formId="inquiry-form-business" sectionName="Multiple Request Forms" defaultValues={{
                companyName: "",
                contactName: "",
                workEmail: "",
                partnershipDetails: ""
              }} submitText="Submit Business Inquiry" successMessage="Thank you for your interest! Our partnerships team will review your inquiry and contact you soon.">
                  <BusinessInquiryForm />
                </FormProvider>
              </CardContent>
            </TabsContent>

            <TabsContent value="recruiting" className="m-0">
              <CardHeader>
                <CardTitle className="font-default text-2xl">Join Our Team</CardTitle>
                <CardDescription>
                  Looking for your next career opportunity? Submit your details below and our recruiting team will be in touch.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <FormProvider formId="inquiry-form-recruiting" sectionName="Multiple Request Forms" defaultValues={{
                fullName: "",
                email: "",
                role: "",
                linkedin: "",
                coverLetter: ""
              }} submitText="Submit Application" successMessage="Thank you for applying! We have received your information and will review it shortly.">
                  <RecruitingForm />
                </FormProvider>
              </CardContent>
            </TabsContent>
          </Card>
        </Tabs>

        <div className="mt-16 text-center border-t border-border pt-12">
          <h3 className="text-2xl font-default font-semibold text-foreground mb-4">
            Prefer to speak directly?
          </h3>
          <p className="text-muted-foreground mb-8 max-w-xl mx-auto">
            Skip the forms and schedule a one-on-one consultation with our experts at a time that works best for you.
          </p>
          <Button size="lg" asChild className="rounded-md shadow-md">
            <Link to="#">
              <SafeHydrate><Calendar className="mr-2 size-5" /></SafeHydrate>
              Schedule Consultation
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}